import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [validToken, setValidToken] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for token in URL hash
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      setValidToken(true);
      // Let Supabase handle the session from the URL
      supabase.auth.getSession().then(({ data }) => {
        if (!data.session) {
          setError('Invalid or expired reset link. Please request a new one.');
        }
      });
    } else {
      setError('Invalid or expired reset link. Please request a new one.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setSuccess('Password updated successfully. Redirecting to login...');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.message || 'Could not update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>Transformation Companion</div>
        <div style={styles.tagline}>Coaching & Counseling</div>
        <h2 style={styles.title}>Set new password</h2>

        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        {validToken && !success && (
          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>New password</label>
              <input
                type="password"
                style={styles.input}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                minLength={8}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm password</label>
              <input
                type="password"
                style={styles.input}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="Repeat your new password"
                required
              />
            </div>
            <button
              type="submit"
              style={{...styles.btn, opacity: loading ? 0.7 : 1}}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update password'}
            </button>
          </form>
        )}

        {!validToken && !error && (
          <p style={styles.muted}>Verifying reset link...</p>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#F8F5F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Figtree', sans-serif",
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '420px',
    border: '1px solid #D8E0DC',
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
  },
  logo: {
    fontFamily: 'Georgia, serif',
    fontSize: '24px',
    fontWeight: '300',
    fontStyle: 'italic',
    color: '#C4956A',
    marginBottom: '4px',
  },
  tagline: {
    fontSize: '11px',
    color: '#8C9E98',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '32px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '500',
    color: '#2D3A35',
    marginBottom: '24px',
  },
  formGroup: {
    marginBottom: '18px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color: '#8C9E98',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    borderRadius: '8px',
    border: '1px solid #D8E0DC',
    background: '#F8F5F0',
    fontFamily: "'Figtree', sans-serif",
    fontSize: '14px',
    color: '#2D3A35',
    outline: 'none',
    boxSizing: 'border-box',
  },
  btn: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    background: '#5C7A6B',
    color: 'white',
    fontFamily: "'Figtree', sans-serif",
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginTop: '8px',
  },
  error: {
    background: '#F5EAEA',
    color: '#B85C5C',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '13.5px',
    marginBottom: '16px',
  },
  success: {
    background: '#EBF0ED',
    color: '#5C7A6B',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '13.5px',
    marginBottom: '16px',
  },
  muted: {
    color: '#8C9E98',
    fontSize: '14px',
    textAlign: 'center',
  },
};